import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { maskFieldValue } from '@ooh/ui/components/masked-value';
import type { CostHistoryEntry } from '@ooh/contracts';

type CostFieldKey =
  | 'catalogPrice'
  | 'rent'
  | 'printCost'
  | 'installCost'
  | 'dismantlingCost'
  | 'productionCost';

interface FieldDelta {
  id: string;
  field: string;
  oldValue: number | null;
  newValue: number | null;
  changedAt: string;
  /**
   * True when this delta's before/after values were withheld by the
   * server's rent-specific gate (rentWithheld) rather than genuinely
   * unchanged — always false for every field other than `rent`
   * (rent-cost-visibility-permission, CU-86cbgnt3z).
   */
  masked: boolean;
}

/**
 * `rent` is force-treated as changed whenever the response marks it
 * withheld: the server nulls rent on every entry for a caller lacking
 * cost.view_rent, so `prev.rent === curr.rent` (both null) would otherwise
 * silently drop the row — the exact "value withheld" case this marker
 * exists to distinguish from "value genuinely unchanged".
 *
 * The migration backfill (CU-86cbc4mjk) fills a row's own `production_cost`
 * from that SAME row's own dormant `print_cost` — never from the
 * predecessor's — so the phantom-fill check below must compare `newVal`
 * against `curr.printCost`. Comparing against `prev.printCost` instead
 * would misfire whenever a pre-consolidation printCost edit lands on the
 * same transition (predecessor and successor printCost legitimately
 * differ), either hiding a real production-cost edit or letting a genuine
 * one show up as a duplicate of the legacy delta.
 */
function fieldChanged(
  key: CostFieldKey,
  oldVal: number | null,
  newVal: number | null,
  prev: CostHistoryEntry,
  curr: CostHistoryEntry,
): boolean {
  if (key === 'rent' && curr.rentWithheld) return true;
  if (
    key === 'productionCost' &&
    prev.productionCost == null &&
    curr.printCost != null &&
    newVal === curr.printCost
  ) {
    return false;
  }
  return oldVal !== newVal;
}

function buildDeltas(
  history: CostHistoryEntry[],
  fieldLabels: Record<string, string>,
): FieldDelta[] {
  const chronological = [...history].reverse();
  const deltas: FieldDelta[] = [];
  for (let i = 1; i < chronological.length; i++) {
    const prev = chronological[i - 1]!;
    const curr = chronological[i]!;
    for (const key of Object.keys(fieldLabels) as CostFieldKey[]) {
      const oldVal = prev[key] ?? null;
      const newVal = curr[key] ?? null;
      if (!fieldChanged(key, oldVal, newVal, prev, curr)) continue;
      deltas.push({
        id: `${curr.id}-${key}`,
        field: fieldLabels[key]!,
        oldValue: oldVal,
        newValue: newVal,
        changedAt: curr.effectiveFrom,
        masked: key === 'rent' && curr.rentWithheld,
      });
    }
  }
  return deltas.reverse();
}

export function CostHistorySection({ history }: { history: CostHistoryEntry[] }) {
  const { t } = useTranslation('inventory');
  const fieldLabels = React.useMemo<Record<string, string>>(
    () => ({
      catalogPrice: t('form.costHistory.catalogPrice'),
      rent: t('form.costHistory.rent'),
      printCost: t('form.costHistory.printCost'),
      installCost: t('form.costHistory.installCost'),
      dismantlingCost: t('form.costHistory.dismantlingCost'),
      productionCost: t('form.costHistory.productionCost'),
    }),
    [t],
  );
  const deltas = buildDeltas(history, fieldLabels);
  const maskedExplanation = t('table.maskedExplanation');
  if (deltas.length === 0) {
    return (
      <div data-testid="cost-history" className="text-[12px] text-muted-foreground italic">
        {t('form.costHistory.noHistory')}
      </div>
    );
  }
  return (
    <div data-testid="cost-history">
      <div className="flex items-center gap-2 mb-3">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-[13px] font-medium text-foreground">
          {t('form.costHistory.title')}
        </span>
      </div>
      <div className="rounded-lg border border-border/50 overflow-hidden">
        <Table className="text-[12px]">
          <TableHeader>
            <TableRow className="border-b border-border/40">
              <TableHead caps={false} className="px-3 py-2 text-left">
                {t('form.costHistory.field')}
              </TableHead>
              <TableHead caps={false} className="px-3 py-2 text-right">
                {t('form.costHistory.previousValue')}
              </TableHead>
              <TableHead caps={false} className="px-3 py-2 text-right">
                {t('form.costHistory.newValue')}
              </TableHead>
              <TableHead caps={false} className="px-3 py-2 text-left">
                {t('form.costHistory.date')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deltas.map((d) => (
              <TableRow key={d.id} className="border-b border-border/30 last:border-b-0">
                <TableCell className="px-3 py-2 text-foreground">{d.field}</TableCell>
                <TableCell className="px-3 py-2 text-right font-mono text-muted-foreground">
                  {maskFieldValue(
                    d.oldValue,
                    d.masked,
                    maskedExplanation,
                    d.oldValue != null ? d.oldValue.toLocaleString('pl-PL') + ' PLN' : '\u2014',
                  )}
                </TableCell>
                <TableCell className="px-3 py-2 text-right font-mono text-foreground font-medium">
                  {maskFieldValue(
                    d.newValue,
                    d.masked,
                    maskedExplanation,
                    d.newValue != null ? d.newValue.toLocaleString('pl-PL') + ' PLN' : '\u2014',
                  )}
                </TableCell>
                <TableCell className="px-3 py-2 text-muted-foreground">{d.changedAt}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
